// Same card spec as backend/mindset.py (_layout/_draw_card) so cards and videos match.
export const SIZES = { square: [1080, 1080], tall: [1080, 1920] };
const RED = "#FD0F10";
const WHITE = "#FFFFFF";
const BOLD = (px) => `bold ${px}px "IHMJ Sans", Arial, sans-serif`;
const REG = (px) => `${px}px "IHMJ Sans", Arial, sans-serif`;

let fontsReady = null;
export const loadCardFonts = () => {
  if (!fontsReady) {
    const faces = [new FontFace("IHMJ Sans", "url(/fonts/LiberationSans-Bold.ttf)", { weight: "700" }),
      new FontFace("IHMJ Sans", "url(/fonts/LiberationSans-Regular.ttf)", { weight: "400" })];
    fontsReady = Promise.all(faces.map((f) => f.load().then((x) => document.fonts.add(x)))).catch(() => { fontsReady = null; });
  }
  return fontsReady;
};

// Words a line should not end on: breaking after them splits a phrase. Same list as backend/mindset.py WEAK_END.
const WEAK_END = new Set(("a an the of to in on at by for from with into onto upon and but or nor so yet as if than that "
  + "is are was were be been am i i'm you we they he she it my your our their his her its this these those "
  + "not no very more most every each any all can will would should could must may might do does did "
  + "who whom whose which what when where while because about over under after before").split(" "));

const lineCost = (width, words, prev, maxW, last) => {
  const target = maxW * 0.8;
  let cost = ((width - target) / maxW) ** 2 + 0.35;
  if (prev && width < prev) cost += 3 * ((prev - width) / maxW) ** 2;
  const end = words[words.length - 1];
  if (!last) {
    const ch = end[end.length - 1];
    if (".!?;:".includes(ch)) cost -= 0.8;
    else if (",\u2014-".includes(ch)) cost -= 0.5;
    else if (WEAK_END.has(end.toLowerCase().replace(/^['"\u2018\u2019\u201c\u201d]+|['"\u2018\u2019\u201c\u201d]+$/g, ""))) cost += 2.5;
  }
  if (words.length === 1) cost += 2.0;
  if (words.length > 5) cost += 0.25 * (words.length - 5) ** 2;
  return cost;
};

const better = (x, y) => !y || x[0] < y[0] || (x[0] === y[0] && x[1] < y[1]);

// Best line breaks for a centred, gently widening (pyramid) shape, never wider than maxW. Mirrors _break_lines().
const breakLines = (words, ww, sp, maxW) => {
  const n = words.length;
  const pre = [0];
  ww.forEach((x) => pre.push(pre[pre.length - 1] + x));
  const width = (a, b) => pre[b] - pre[a] + sp * (b - a - 1);
  const best = new Map();
  const ending = Array.from({ length: n + 1 }, () => []);
  for (let a = 0; a < n; a++) {
    if (a && !ending[a].length) continue;
    for (let b = a + 1; b <= n; b++) {
      const wl = width(a, b);
      if (wl > maxW) break;
      let cand = null;
      if (a === 0) cand = [lineCost(wl, words.slice(0, b), 0, maxW, b === n), -1];
      else ending[a].forEach((k) => {
        const c = [best.get(`${k},${a}`)[0] + lineCost(wl, words.slice(a, b), width(k, a), maxW, b === n), k];
        if (better(c, cand)) cand = c;
      });
      best.set(`${a},${b}`, cand);
      ending[b].push(a);
    }
  }
  if (!ending[n].length) return null;
  let pick = null;
  ending[n].forEach((a) => { const c = [best.get(`${a},${n}`)[0], a]; if (better(c, pick)) pick = c; });
  const [total] = pick;
  let a = pick[1];
  let b = n;
  const lines = [];
  while (a >= 0) {
    lines.unshift(words.slice(a, b));
    const k = best.get(`${a},${b}`)[1];
    b = a;
    a = k;
  }
  return { lines, cost: total };
};

export const layout = (ctx, text, size, handle) => {
  const [w, h] = SIZES[size];
  const pad = Math.round(w * 0.09);
  const markFs = Math.round(w * 0.06);
  const markY = h - Math.round(h * (size === "tall" ? 0.06 : 0.05)) - markFs;
  const top = Math.round(h * 0.07);
  const bottom = markY - Math.round(h * 0.06);
  const words = text.split(/\s+/).filter(Boolean);
  const fit = (fs) => {
    ctx.font = BOLD(fs);
    const res = breakLines(words, words.map((x) => ctx.measureText(x).width), ctx.measureText(" ").width, w - 2 * pad);
    if (!res) return null;
    const lh = Math.round(fs * 1.18);
    const afs = Math.max(34, Math.min(60, Math.round(fs * 0.42)));
    const hfs = Math.round(afs * 0.72);
    const barGap = Math.round(fs * 0.55);
    const authGap = Math.max(36, Math.round(fs * 0.6));
    const block = 10 + barGap + res.lines.length * lh + authGap + Math.round(afs * 1.25) + (handle ? Math.round(hfs * 1.6) : 0);
    return { w, h, fs, lines: res.lines, cost: res.cost, lh, afs, hfs, barGap, authGap, block, markFs, markY, ok: block <= bottom - top };
  };
  // Biggest size that fits, or up to ~40% smaller when that gives much nicer line breaks.
  let first = null;
  let best = null;
  for (let fs = Math.round(w * 0.15); fs >= 34; fs -= 2) {
    const r = fit(fs);
    if (!r || !r.ok) { if (first === null) continue; break; }
    first = first || fs;
    if (fs < first * 0.6) break;
    const score = r.cost + 6 * (1 - fs / first);
    if (!best || score < best[0]) best = [score, r];
  }
  const L = best ? best[1] : fit(34);
  const y = Math.max(top, Math.round((h - L.block) / 2));
  return { ...L, barY: y, textY: y + 10 + L.barGap, authY: y + 10 + L.barGap + L.lines.length * L.lh + L.authGap };
};

const centred = (ctx, str, y, w) => ctx.fillText(str, (w - ctx.measureText(str).width) / 2, y);

const normWord = (t) => t.replace(/^[^\p{L}\p{N}_]+|[^\p{L}\p{N}_]+$/gu, "").toLowerCase();
// Position of the first word matching the red keyword, or -1. Mirrors key_index() in backend/mindset.py.
export const keyIndex = (text, keyword) => {
  const k = normWord(keyword || "");
  return k ? text.split(/\s+/).filter(Boolean).findIndex((t) => normWord(t) === k) : -1;
};

// shown = number of quote words visible; showAuthor toggles author + handle; zoom scales the quote block only.
export const drawFrame = (ctx, L, { author, handle, keyword }, shown, showAuthor, zoom = 1) => {
  const { w, h } = L;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, w, h);
  ctx.textBaseline = "top";
  ctx.setTransform(zoom, 0, 0, zoom, (w - w * zoom) / 2, (h - h * zoom) / 2);
  const bw = Math.round(w * 0.08);
  ctx.fillStyle = RED;
  ctx.fillRect((w - bw) / 2, L.barY, bw, 10);
  ctx.font = BOLD(L.fs);
  const red = keyIndex(L.lines.flat().join(" "), keyword);
  let k = 0;
  let left = shown;
  L.lines.forEach((ln, i) => {
    const x0 = (w - ctx.measureText(ln.join(" ")).width) / 2;
    ln.forEach((tok, j) => {
      if (j < left) {
        const x = x0 + (j ? ctx.measureText(`${ln.slice(0, j).join(" ")} `).width : 0);
        const y = L.textY + i * L.lh;
        if (k === red) {
          // Only the word itself is red; punctuation around it stays white.
          const [, pre, core, post] = tok.match(/^([^\p{L}\p{N}_]*)(.*?)([^\p{L}\p{N}_]*)$/u);
          [[pre, WHITE, ""], [core, RED, pre], [post, WHITE, pre + core]].forEach(([part, col, off]) => {
            if (part) { ctx.fillStyle = col; ctx.fillText(part, x + (off ? ctx.measureText(off).width : 0), y); }
          });
        } else {
          ctx.fillStyle = WHITE;
          ctx.fillText(tok, x, y);
        }
      }
      k += 1;
    });
    left -= ln.length;
  });
  if (showAuthor) {
    ctx.font = BOLD(L.afs);
    ctx.fillStyle = RED;
    centred(ctx, `\u2014 ${author}`, L.authY, w);
    if (handle) {
      ctx.font = REG(L.hfs);
      ctx.fillStyle = WHITE;
      centred(ctx, handle, L.authY + Math.round(L.afs * 1.25) + Math.round(L.hfs * 0.3), w);
    }
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.font = BOLD(L.markFs);
  const parts = [["ihate", WHITE], ["myjob", RED], [".app", WHITE]];
  let x = (w - parts.reduce((s, [p]) => s + ctx.measureText(p).width, 0)) / 2;
  parts.forEach(([p, c]) => { ctx.fillStyle = c; ctx.fillText(p, x, L.markY); x += ctx.measureText(p).width; });
};
